/**
 * WorkflowPrinter — staff workflow interface on the Label Printer page.
 * Toggles between Kitchen Prep and Retail Sale modes, pulls items from
 * the dual-category product library, and prints 60×50 mm labels to the
 * Tecor TR-422B over WebUSB (browser-print fallback).
 *
 *  - Kitchen Prep : auto Use-By from shelf life, staff id, + a BATCH
 *                   QUEUE so several items print in one Tecor run.
 *  - Retail Sale  : price override, best-before, ingredients, bolded
 *                   allergens, GTIN/EAN barcode.
 *
 * Every successful print is recorded to the print audit log.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { withSite, formatErr } from "@/lib/api";
import { useTecor } from "@/contexts/TecorContext";
import { logPrint } from "@/lib/printLog";
import { enqueuePrint, haptic } from "@/lib/printHub";
import { DEFAULT_PREP_TEMPLATE, DEFAULT_RETAIL_TEMPLATE } from "@/lib/labelFields";
import WorkflowLabelPreview from "@/components/workflow/WorkflowLabelPreview";
import CanvasLabelView from "@/components/workflow/CanvasLabelView";
import SortableList from "@/components/workflow/SortableList";
import BatchOrderDialog from "@/components/workflow/BatchOrderDialog";
import { declarationName, ALLERGENS } from "@/lib/allergens";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Usb, FlaskConical, Plug, CheckCircle2, AlertTriangle,
  ListPlus, Trash2, Layers, Save, Zap, Send, Radio, Clock, ExternalLink, Minus, Plus,
  GripVertical, ListOrdered,
} from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const ALLERGEN_SHORT = {
  gluten: "Gluten", crustacea: "Crustacea", egg: "Egg", fish: "Fish",
  milk: "Milk", peanut: "Peanut", sesame: "Sesame", soy: "Soy",
  tree_nut: "Tree nuts", lupin: "Lupin", mollusc: "Mollusc", sulphites: "Sulphites",
};
const shortAllergen = (id) => ALLERGEN_SHORT[id] || id;

const STORAGE_LABEL = {
  refrigerated: "REFRIGERATED", frozen: "FROZEN",
  ambient: "AMBIENT", hot_hold: "HOT HOLD",
};

function fmtDateTime(iso) {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleString("en-AU", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}
function fmtDate(iso) {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function addHours(iso, hours) {
  const d = iso ? new Date(iso) : new Date();
  d.setHours(d.getHours() + Number(hours || 0));
  return d;
}
function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
}

export default function WorkflowPrinter({ mode, activeSite, user }) {
  const isRetail = mode === "retail";
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [copies, setCopies] = useState(1);
  const [queue, setQueue] = useState([]); // prep-only session queue
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  const [prepDate, setPrepDate] = useState(new Date().toISOString().slice(0, 16));
  const [staffId, setStaffId] = useState(user?.name || "");
  const [storage, setStorage] = useState("refrigerated");
  const [priceOverride, setPriceOverride] = useState("");
  const [bestBefore, setBestBefore] = useState("");
  const [ingredients, setIngredients] = useState("");
  // Retail one-off print edits — every element editable except the barcode.
  const [nameOverride, setNameOverride] = useState("");
  const [allergenSel, setAllergenSel] = useState([]);
  const [mayContainSel, setMayContainSel] = useState([]);
  const [extraAllergens, setExtraAllergens] = useState("");
  const [saveBack, setSaveBack] = useState(false);

  const { connected, connect } = useTecor();
  const [sending, setSending] = useState(false);
  const [lastQueued, setLastQueued] = useState(null);   // {name, at}
  const [hubRecent, setHubRecent] = useState([]);        // recent queue jobs (last printed)

  // Poll the Print Hub queue so staff get feedback that the master
  // station picked up + printed their label.
  useEffect(() => {
    if (!activeSite?.id) return undefined;
    let alive = true;
    const load = () =>
      api.get("/print-queue/recent", { params: { site_id: activeSite.id, limit: 10 } })
        .then((r) => { if (alive) setHubRecent(r.data || []); })
        .catch(() => {});
    load();
    const t = setInterval(load, 4000);
    return () => { alive = false; clearInterval(t); };
  }, [activeSite?.id]);

  const hubPending = hubRecent.filter((j) => j.status === "pending" || j.status === "printing").length;
  const lastPrinted = hubRecent.find((j) => j.status === "completed");

  const template = isRetail
    ? (activeSite?.retail_template || DEFAULT_RETAIL_TEMPLATE)
    : (activeSite?.prep_template || DEFAULT_PREP_TEMPLATE);

  const selected = useMemo(
    () => products.find((p) => p.id === selectedId) || null,
    [products, selectedId],
  );

  useEffect(() => {
    if (!activeSite) return;
    setQueue([]); // reset queue on site/mode change
    api.get("/products", withSite(activeSite.id, { category: mode }))
      .then((r) => {
        setProducts(r.data || []);
        setSelectedId((r.data && r.data[0]?.id) || "");
      })
      .catch(() => setProducts([]));
  }, [activeSite, mode]);

  useEffect(() => {
    if (!activeSite || isRetail) { setPresets([]); return; }
    api.get("/queue-presets", withSite(activeSite.id))
      .then((r) => setPresets(r.data || []))
      .catch(() => setPresets([]));
  }, [activeSite, isRetail]);

  useEffect(() => {
    if (!selected) return;
    setStorage(selected.storage || "refrigerated");
    if (isRetail) {
      setPriceOverride(String(selected.price_aud ?? ""));
      setIngredients(selected.ingredients || "");
      setBestBefore(addDaysISO(selected.best_before_days || 0));
      setNameOverride(selected.name || "");
      setAllergenSel(selected.allergens || []);
      setMayContainSel(selected.may_contain || []);
      setExtraAllergens("");
      setSaveBack(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const useByISO = useMemo(
    () => addHours(prepDate, selected?.shelf_life_hours || 48),
    [prepDate, selected],
  );

  const extraTokens = extraAllergens.split(",").map((s) => s.trim()).filter(Boolean);
  const allergensShort = isRetail ? [...allergenSel, ...extraTokens] : (selected?.allergens || []);
  const mayContainShort = isRetail ? mayContainSel : (selected?.may_contain || []);
  const labelName = isRetail ? (nameOverride.trim() || selected?.name || "") : (selected?.name || "");

  // When the site has a canvas layout enabled for this label type (or the
  // selected product is bound to a saved canvas preset), the Tecor prints
  // that WYSIWYG layout — so the preview must render the SAME canvas, not
  // the simple stacked template, otherwise "the label doesn't match the
  // preview".
  const canvasLayout = useMemo(() => {
    if (!activeSite || (mode !== "prep" && mode !== "retail")) return null;
    const presets = activeSite.canvas_layout_presets || {};
    if (selected?.canvas_preset_id) {
      const p = (presets[mode] || []).find((x) => x.id === selected.canvas_preset_id);
      if (p?.layout?.elements) return p.layout;
    }
    const def = (activeSite.canvas_layouts || {})[mode];
    return def && def.enabled && def.elements ? def : null;
  }, [activeSite, selected, mode]);

  // Trust-page QR link for retail. Sent when the site's public Trust page
  // is enabled OR the active canvas layout explicitly contains a Trust QR
  // element (the admin dragged one on and expects it to print).
  const trustUrl = useMemo(() => {
    if (!isRetail || !activeSite) return "";
    const hasTrustEl = (canvasLayout?.elements || []).some((e) => e.type === "qr" && e.source === "trust");
    if (activeSite.public_trust_enabled || hasTrustEl) return `${window.location.origin}/trust/${activeSite.id}`;
    return "";
  }, [isRetail, activeSite, canvasLayout]);

  const preview = useMemo(() => {
    if (!selected) return null;
    return {
      type: mode,
      template,
      site: activeSite?.name || "",
      product: labelName,
      prep_date: fmtDateTime(prepDate),
      use_by_date: fmtDateTime(useByISO),
      staff_id: staffId,
      storage: STORAGE_LABEL[storage] || storage,
      allergens: allergensShort,
      price: isRetail ? (priceOverride === "" ? selected.price_aud : Number(priceOverride)) : null,
      best_before: isRetail ? fmtDate(bestBefore) : "",
      ingredients: isRetail ? ingredients : "",
      barcode: isRetail ? (selected.barcode || "") : "",
      nutrition: isRetail ? (selected.nutrition || null) : null,
      trust_url: trustUrl,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, mode, activeSite, prepDate, useByISO, staffId, storage, priceOverride, bestBefore, ingredients, template, trustUrl, nameOverride, allergenSel, mayContainSel, extraAllergens]);

  const canvasData = useMemo(() => {
    if (!selected) return null;
    const al = (allergensShort || []).map(declarationName);
    const mc = (mayContainShort || []).map(declarationName);
    const decl = [];
    if (al.length) decl.push("Contains: " + al.join(", ") + ".");
    if (mc.length) decl.push("May be present: " + mc.join(", ") + ".");
    const priceNum = priceOverride === "" ? selected.price_aud : Number(priceOverride);
    return {
      site_name: activeSite?.name || "",
      product_name: labelName,
      allergens: (allergensShort || []).map((a) => String(a).toUpperCase()).join(", "),
      allergen_declaration: decl.join("\n"),
      storage: STORAGE_LABEL[storage] || storage,
      price: isRetail && priceNum != null && priceNum !== "" ? `$${Number(priceNum).toFixed(2)}` : "",
      best_before: isRetail ? fmtDate(bestBefore) : "",
      ingredients: isRetail ? ingredients : "",
      barcode: isRetail ? (selected.barcode || "") : "",
      prep_date: !isRetail ? fmtDateTime(prepDate) : "",
      use_by_date: !isRetail ? fmtDateTime(useByISO) : "",
      staff_id: staffId,
      trust_url: trustUrl,
      qr_url: "",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, mode, activeSite, prepDate, useByISO, staffId, storage, priceOverride, bestBefore, ingredients, allergensShort, mayContainShort, trustUrl, nameOverride]);

  const buildPayload = (labelType) => {
    if (labelType === "test") return { label_type: "test", copies: Number(copies) || 1 };
    if (!selected) return null;
    if (isRetail) {
      return {
        label_type: "retail",
        product_name: labelName,
        price_aud: priceOverride === "" ? selected.price_aud : Number(priceOverride),
        best_before: fmtDate(bestBefore),
        ingredients,
        barcode: selected.barcode || "",
        allergens: allergensShort,
        storage,
        may_contain: mayContainShort,
        nutrition: selected.nutrition || undefined,
        canvas_preset_id: selected.canvas_preset_id || undefined,
        // Public trust-page QR — sent when the venue opted in OR the
        // canvas layout explicitly includes a Trust QR element.
        trust_url: trustUrl || undefined,
        copies: Number(copies) || 1,
      };
    }
    return {
      label_type: "prep",
      product_name: selected.name,
      prep_date: fmtDateTime(prepDate),
      use_by_date: fmtDateTime(useByISO),
      staff_id: staffId,
      allergens: allergensShort,
      storage,
      may_contain: mayContainShort,
      canvas_preset_id: selected.canvas_preset_id || undefined,
      copies: Number(copies) || 1,
    };
  };

  const renderBytes = async (labelType) => {
    const payload = buildPayload(labelType);
    if (!payload) {
      toast.error("Pick a product first");
      return null;
    }
    const { data } = await api.post("/labels/render-tspl", payload, withSite(activeSite.id));
    if (!data?.bytes_b64) {
      toast.error("Couldn't render label (Training Mode blocks printing).");
      return null;
    }
    return data.bytes_b64;
  };

  const handleConnect = async () => {
    try {
      await connect();
      toast.success("Tecor TR-422B connected");
    } catch (err) {
      toast.error(err.message || "Connection cancelled");
    }
  };

  const sendToHub = async (labelType) => {
    const payload = buildPayload(labelType);
    if (!payload) {
      toast.error("Pick a product first");
      return;
    }
    const name = labelType === "test" ? "Test label" : (labelName || selected?.name || "Label");
    setSending(true);
    try {
      await enqueuePrint(activeSite.id, {
        label_type: labelType,
        product_name: labelType === "test" ? "Test label" : name,
        copies: payload.copies,
        payload,
      });
      haptic();
      setLastQueued({ name, at: Date.now() });
      if (labelType !== "test") {
        logPrint(activeSite.id, {
          product_name: name, label_type: mode, method: "tecor", copies,
          payload,
        });
      }
      toast.success(`Sent to Print Hub · ${name} ×${payload.copies}`);
      if (labelType === "retail" && saveBack && selected) {
        try {
          await api.patch(`/products/${selected.id}`, {
            name: labelName,
            price_aud: priceOverride === "" ? selected.price_aud : Number(priceOverride),
            ingredients,
            allergens: allergenSel,
            may_contain: mayContainSel,
            storage,
          }, withSite(activeSite.id));
          const patched = {
            ...selected, name: labelName, ingredients, storage,
            price_aud: priceOverride === "" ? selected.price_aud : Number(priceOverride),
            allergens: allergenSel, may_contain: mayContainSel,
          };
          setProducts((ps) => ps.map((p) => (p.id === selected.id ? patched : p)));
          setSaveBack(false);
          toast.success("Changes saved to the product");
        } catch (err) {
          toast.error(formatErr(err.response?.data?.detail) || "Label sent, but couldn't save changes to the product");
        }
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message || "Couldn't queue label");
    } finally {
      setSending(false);
    }
  };

  // ---------- Batch queue (prep only) ----------
  const addToQueue = () => {
    if (!selected) {
      toast.error("Pick a prep item first");
      return;
    }
    setQueue((q) => [
      ...q,
      {
        uid: `${selected.id}-${Date.now()}`,
        product_id: selected.id,
        payload: buildPayload("prep"),
        name: selected.name,
        use_by: fmtDateTime(useByISO),
        copies: Number(copies) || 1,
      },
    ]);
    toast.success(`${selected.name} added to queue`);
  };
  const removeFromQueue = (uid) => setQueue((q) => q.filter((x) => x.uid !== uid));

  // Bump/lower a queued item's copy count in place (keeps the item's
  // payload.copies in sync so the Hub prints the right quantity).
  const setQueueItemCopies = (uid, next) =>
    setQueue((qs) =>
      qs.map((q) => {
        if (q.uid !== uid) return q;
        const copies = Math.max(1, Math.min(99, Number(next) || 1));
        return { ...q, copies, payload: { ...q.payload, copies } };
      }),
    );

  // ---------- Queue presets (saved "morning batch") ----------
  const reloadPresets = () =>
    api.get("/queue-presets", withSite(activeSite.id))
      .then((r) => setPresets(r.data || []))
      .catch(() => {});

  const savePreset = async () => {
    if (!queue.length) { toast.error("Add items to the queue first"); return; }
    if (!presetName.trim()) { toast.error("Name the batch first"); return; }
    try {
      await api.post("/queue-presets", {
        name: presetName.trim(),
        items: queue.map((q) => ({ product_id: q.product_id, product_name: q.name, copies: q.copies })),
      }, withSite(activeSite.id));
      setPresetName("");
      reloadPresets();
      toast.success("Batch saved");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const buildPresetItems = (preset) => {
    const items = [];
    const missing = [];
    preset.items.forEach((it) => {
      const p = products.find((x) => x.id === it.product_id);
      if (!p) { missing.push(it.product_name || "item"); return; }
      const useBy = addHours(prepDate, p.shelf_life_hours || 48);
      items.push({
        uid: `${p.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        product_id: p.id,
        name: p.name,
        use_by: fmtDateTime(useBy),
        copies: it.copies || 1,
        payload: {
          label_type: "prep",
          product_name: p.name,
          prep_date: fmtDateTime(prepDate),
          use_by_date: fmtDateTime(useBy),
          staff_id: staffId,
          allergens: (p.allergens || []),
          storage: p.storage || "refrigerated",
          may_contain: (p.may_contain || []),
          canvas_preset_id: p.canvas_preset_id || undefined,
          copies: it.copies || 1,
        },
      });
    });
    return { items, missing };
  };

  const loadPreset = (preset) => {
    const { items, missing } = buildPresetItems(preset);
    setQueue(items);
    if (missing.length) toast.error(`Skipped ${missing.length} item(s) no longer in the library`);
    if (items.length) toast.success(`Loaded "${preset.name}" (${items.length} items)`);
  };

  // One-tap: load the saved batch AND queue the whole run to the Hub.
  const loadAndPrintPreset = async (preset) => {
    const { items, missing } = buildPresetItems(preset);
    setQueue(items);
    if (missing.length) toast.error(`Skipped ${missing.length} item(s) no longer in the library`);
    if (!items.length) return;
    await enqueueItems(items, `Queued "${preset.name}"`);
  };

  const deletePreset = async () => {
    const p = deleteTarget;
    if (!p) return;
    try {
      await api.delete(`/queue-presets/${p.id}`, withSite(activeSite.id));
      reloadPresets();
      toast.success("Batch deleted");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setDeleteTarget(null);
    }
  };

  const enqueueItems = async (items, successMsg) => {
    if (!items.length) return;
    setSending(true);
    try {
      // Push each label to the Hub queue; the master station prints them.
      for (const q of items) {
        // eslint-disable-next-line no-await-in-loop
        await enqueuePrint(activeSite.id, {
          label_type: q.payload.label_type || "prep",
          product_name: q.name,
          copies: q.copies,
          payload: q.payload,
        });
        logPrint(activeSite.id, {
          product_name: q.name, label_type: "prep", method: "tecor", copies: q.copies,
          payload: q.payload,
        });
      }
      haptic(90);
      setLastQueued({ name: `${items.length} labels`, at: Date.now() });
      toast.success(`${successMsg} — ${items.length} labels sent to Print Hub`);
      setQueue([]);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message || "Couldn't queue batch");
    } finally {
      setSending(false);
    }
  };

  const printQueue = async () => {
    if (!queue.length) return;
    await enqueueItems(queue, "Queued batch");
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 surface p-6 md:p-8 space-y-5 no-print">
        {/* Print Hub status — labels are sent to the master station queue */}
        <div className="rounded-lg border p-3 space-y-2" data-testid="hub-status" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Radio className="w-4 h-4 text-ochre-600" />
              <span className="font-medium">Prints via Print Hub</span>
              {hubPending > 0 && (
                <span className="text-xs font-mono bg-amber-100 text-amber-800 rounded-full px-2 py-0.5" data-testid="hub-pending-badge">
                  {hubPending} in queue
                </span>
              )}
            </div>
            <Button
              data-testid="wf-test-print"
              size="sm" variant="ghost" className="gap-2 text-eucalyptus-800"
              disabled={sending}
              onClick={() => sendToHub("test")}
            >
              <FlaskConical className="w-4 h-4" /> Test print
            </Button>
          </div>
          <p className="text-[11px] text-charcoal-soft">
            No printer needed on this device — labels print at the station computer connected to the Tecor.
            {user?.role === "admin" && (
              <> {" "}
                <Link to="/print-hub" className="text-ochre-700 hover:underline inline-flex items-center gap-0.5" data-testid="wf-open-hub">
                  Open Print Hub <ExternalLink className="w-3 h-3" />
                </Link>
              </>
            )}
          </p>
          {lastQueued && (
            <div className="flex items-center gap-1.5 text-[11px] text-eucalyptus-800" data-testid="wf-last-queued">
              <CheckCircle2 className="w-3.5 h-3.5" /> Sent {lastQueued.name} · just now
            </div>
          )}
          {lastPrinted && (
            <div className="flex items-center gap-1.5 text-[11px] text-charcoal-soft" data-testid="wf-last-printed">
              <Clock className="w-3.5 h-3.5" /> Last printed: {lastPrinted.product_name || lastPrinted.label_type} at{" "}
              {new Date(lastPrinted.printed_at || lastPrinted.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label>{isRetail ? "Retail product" : "Prep item"}</Label>
          <Select key={`prod-${mode}-${products.length}`} value={selectedId || undefined} onValueChange={setSelectedId}>
            <SelectTrigger data-testid="wf-product" className="h-12">
              <SelectValue placeholder={products.length ? "Choose a product\u2026" : "No products \u2014 add some in the Products page"} />
            </SelectTrigger>
            <SelectContent>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selected && (
          <div className="grid md:grid-cols-2 gap-4">
            {isRetail ? (
              <>
                <div className="space-y-2 md:col-span-2">
                  <Label>Product name — editable for this label</Label>
                  <Input data-testid="wf-name-input" className="h-12 font-medium" value={nameOverride} onChange={(e) => setNameOverride(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Price (AUD) — override</Label>
                  <Input data-testid="wf-price-input" type="number" step="0.01" min="0" className="h-12 font-mono" value={priceOverride} onChange={(e) => setPriceOverride(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Best before</Label>
                  <Input data-testid="wf-bestbefore" type="date" className="h-12" value={bestBefore} onChange={(e) => setBestBefore(e.target.value)} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Ingredients</Label>
                  <Textarea data-testid="wf-ingredients" className="min-h-[80px]" value={ingredients} onChange={(e) => setIngredients(e.target.value)} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Contains — tap to toggle</Label>
                  <div className="flex flex-wrap gap-1.5" data-testid="wf-allergen-chips">
                    {ALLERGENS.map((a) => {
                      const on = allergenSel.includes(a.id);
                      return (
                        <button
                          key={a.id} type="button" data-testid={`wf-allergen-${a.id}`}
                          onClick={() => setAllergenSel((s) => (on ? s.filter((x) => x !== a.id) : [...s, a.id]))}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-red-600 text-white border-red-600" : "bg-white text-charcoal border-[color:var(--border)] hover:border-red-400"}`}
                        >
                          {declarationName(a.id)}
                        </button>
                      );
                    })}
                  </div>
                  <Input
                    data-testid="wf-extra-allergens"
                    placeholder="Other allergens — free text, comma-separated (e.g. Royal jelly)"
                    className="h-9 text-sm"
                    value={extraAllergens}
                    onChange={(e) => setExtraAllergens(e.target.value)}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>May be present — tap to toggle</Label>
                  <div className="flex flex-wrap gap-1.5" data-testid="wf-maycontain-chips">
                    {ALLERGENS.map((a) => {
                      const on = mayContainSel.includes(a.id);
                      return (
                        <button
                          key={a.id} type="button" data-testid={`wf-maycontain-${a.id}`}
                          onClick={() => setMayContainSel((s) => (on ? s.filter((x) => x !== a.id) : [...s, a.id]))}
                          className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-amber-500 text-white border-amber-500" : "bg-white text-charcoal border-[color:var(--border)] hover:border-amber-400"}`}
                        >
                          {declarationName(a.id)}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Storage</Label>
                  <Select value={storage} onValueChange={setStorage}>
                    <SelectTrigger data-testid="wf-storage" className="h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="refrigerated">Refrigerated ≤ 5°C</SelectItem>
                      <SelectItem value="frozen">Frozen ≤ -15°C</SelectItem>
                      <SelectItem value="hot_hold">Hot hold ≥ 60°C</SelectItem>
                      <SelectItem value="ambient">Ambient</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Barcode (GTIN / EAN) — locked</Label>
                  <Input data-testid="wf-barcode-input" className="h-12 font-mono bg-eucalyptus-50" value={selected.barcode || ""} readOnly />
                  <p className="text-xs text-charcoal-soft">Stays fixed to this product — edit the GTIN in the Products page.</p>
                </div>
                <label className="flex items-center gap-2 text-sm md:col-span-2 cursor-pointer select-none">
                  <Checkbox data-testid="wf-save-back" checked={saveBack} onCheckedChange={(v) => setSaveBack(!!v)} />
                  Save these changes back to the product (otherwise one-off for this print)
                </label>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Prep date/time</Label>
                  <Input data-testid="wf-prep" type="datetime-local" className="h-12" value={prepDate} onChange={(e) => setPrepDate(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Use-by (auto from {selected.shelf_life_hours}h shelf life)</Label>
                  <Input data-testid="wf-useby" className="h-12 font-mono" value={fmtDateTime(useByISO)} readOnly />
                </div>
                <div className="space-y-2">
                  <Label>Staff ID</Label>
                  <Input data-testid="wf-staff" className="h-12" value={staffId} onChange={(e) => setStaffId(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Storage</Label>
                  <Select value={storage} onValueChange={setStorage}>
                    <SelectTrigger data-testid="wf-storage" className="h-12"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="refrigerated">Refrigerated ≤ 5°C</SelectItem>
                      <SelectItem value="frozen">Frozen ≤ -15°C</SelectItem>
                      <SelectItem value="hot_hold">Hot hold ≥ 60°C</SelectItem>
                      <SelectItem value="ambient">Ambient</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Copies</Label>
              <Input data-testid="wf-copies" type="number" min="1" max="20" className="h-12 w-24" value={copies} onChange={(e) => setCopies(parseInt(e.target.value || "1", 10))} />
            </div>
          </div>
        )}

        {!isRetail && presets.length > 0 && (
          <div className="space-y-2" data-testid="wf-presets">
            <Label className="text-xs uppercase tracking-wide text-charcoal-soft">Saved batches — load, ⚡ to load &amp; print, or reorder items</Label>
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-eucalyptus-100 text-eucalyptus-900 text-sm pl-3 pr-1 py-1" data-testid={`wf-preset-${p.id}`}>
                  <button data-testid={`wf-preset-load-${p.id}`} onClick={() => loadPreset(p)} className="font-medium hover:underline">
                    {p.name} ({p.items.length})
                  </button>
                  <button data-testid={`wf-preset-print-${p.id}`} onClick={() => loadAndPrintPreset(p)} disabled={sending} className="p-1 text-ochre-700 hover:text-ochre-500 disabled:opacity-40" title="Load &amp; send this batch to the Print Hub" aria-label="Send batch to Print Hub">
                    <Zap className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`wf-preset-reorder-${p.id}`} onClick={() => setEditTarget(p)} className="p-1 text-eucalyptus-800 hover:text-eucalyptus-600" title="Reorder items in this batch" aria-label="Reorder batch items">
                    <ListOrdered className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`wf-preset-delete-${p.id}`} onClick={() => setDeleteTarget(p)} className="p-1 text-red-600 hover:text-red-800" aria-label="Delete batch">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3 justify-end pt-2">
          {!isRetail && (
            <Button data-testid="wf-add-queue" variant="outline" className="h-12 gap-2 mr-auto border-eucalyptus-700 text-eucalyptus-900" onClick={addToQueue}>
              <ListPlus className="w-4 h-4" /> Add to queue
            </Button>
          )}
          <Button data-testid="wf-print-tecor" disabled={sending} className="h-12 bg-ochre-600 hover:bg-ochre-400 text-white gap-2" onClick={() => sendToHub(mode)}>
            <Send className="w-4 h-4" /> {sending ? "Sending\u2026" : "Batch print"}
          </Button>
        </div>

        {/* Batch queue (prep only) */}
        {!isRetail && queue.length > 0 && (
          <div className="rounded-lg border p-4 space-y-3" data-testid="wf-queue" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Layers className="w-4 h-4 text-eucalyptus-700" /> Print queue ({queue.length})
              <span className="ml-auto text-[11px] font-normal text-charcoal-soft">Drag to reorder</span>
            </div>
            <SortableList
              items={queue}
              getId={(q) => q.uid}
              onReorder={setQueue}
              className="space-y-2"
              renderItem={(q, dragHandleProps) => (
                <div className="flex items-center justify-between gap-2 text-sm bg-eucalyptus-50 rounded px-3 py-2" data-testid={`wf-queue-item-${q.uid}`}>
                  <button
                    data-testid={`wf-queue-handle-${q.uid}`}
                    {...dragHandleProps}
                    className="text-charcoal-soft hover:text-eucalyptus-900 shrink-0"
                    aria-label="Drag to reorder"
                  >
                    <GripVertical className="w-4 h-4" />
                  </button>
                  <span className="truncate min-w-0 flex-1">
                    <b>{q.name}</b> · use-by {q.use_by}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1" data-testid={`wf-queue-copies-${q.uid}`}>
                      <button
                        data-testid={`wf-queue-copies-minus-${q.uid}`}
                        onClick={() => setQueueItemCopies(q.uid, q.copies - 1)}
                        disabled={q.copies <= 1}
                        className="w-6 h-6 grid place-items-center rounded border border-eucalyptus-700/40 text-eucalyptus-900 hover:bg-eucalyptus-100 disabled:opacity-30"
                        aria-label="Fewer copies"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center font-mono font-semibold tabular-nums" data-testid={`wf-queue-copies-val-${q.uid}`}>{q.copies}×</span>
                      <button
                        data-testid={`wf-queue-copies-plus-${q.uid}`}
                        onClick={() => setQueueItemCopies(q.uid, q.copies + 1)}
                        disabled={q.copies >= 99}
                        className="w-6 h-6 grid place-items-center rounded border border-eucalyptus-700/40 text-eucalyptus-900 hover:bg-eucalyptus-100 disabled:opacity-30"
                        aria-label="More copies"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button data-testid={`wf-queue-remove-${q.uid}`} onClick={() => removeFromQueue(q.uid)} className="text-red-600 hover:text-red-800" aria-label="Remove from queue">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            />
            <div className="flex items-center gap-2 pt-1">
              <Input
                data-testid="wf-preset-name"
                placeholder="Save as batch (e.g. Morning prep)"
                className="h-9 text-sm"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
              />
              <Button data-testid="wf-preset-save" size="sm" variant="outline" className="gap-1 whitespace-nowrap" onClick={savePreset}>
                <Save className="w-4 h-4" /> Save batch
              </Button>
            </div>
            <div className="flex justify-end gap-2">
              <Button data-testid="wf-queue-clear" size="sm" variant="ghost" onClick={() => setQueue([])}>Clear</Button>
              <Button data-testid="wf-queue-print" size="sm" className="bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white gap-2" disabled={sending} onClick={printQueue}>
                <Send className="w-4 h-4" /> Send queue ({queue.length})
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="lg:col-span-2">
        <div className="label-eyebrow mb-3 no-print">
          {isRetail ? "Retail label" : "Prep label"} · 60mm × 50mm
        </div>
        {preview ? (
          canvasLayout && canvasData ? (
            <div data-testid="wf-canvas-active">
              <CanvasLabelView layout={canvasLayout} data={canvasData} />
              <p className="text-[11px] text-charcoal-soft mt-2 no-print">
                Showing your Canvas Designer layout (used for printing). Edit it in Product Library → Canvas Designer.
              </p>
            </div>
          ) : (
            <WorkflowLabelPreview preview={preview} realSize />
          )
        ) : (
          <div className="surface p-8 text-center text-charcoal-soft text-sm" data-testid="wf-empty">
            Select a {isRetail ? "retail" : "prep"} product to preview its label.
          </div>
        )}
      </div>

      {/* Reorder items within a saved morning batch */}
      <BatchOrderDialog
        preset={editTarget}
        siteId={activeSite?.id}
        onClose={() => setEditTarget(null)}
        onSaved={(updated) => setPresets((ps) => ps.map((p) => (p.id === updated.id ? updated : p)))}
      />

      {/* Styled delete-batch confirm (replaces native window.confirm) */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="wf-preset-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this batch?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `"${deleteTarget.name}" (${deleteTarget.items?.length || 0} item${(deleteTarget.items?.length || 0) === 1 ? "" : "s"}) will be removed. This can't be undone.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="wf-preset-delete-cancel">Keep batch</AlertDialogCancel>
            <AlertDialogAction
              data-testid="wf-preset-delete-confirm"
              onClick={deletePreset}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              Delete batch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
